import { createRoot } from 'react-dom/client';
import * as React from 'react';
import { useHydrateAtoms } from 'jotai/utils';
import { NewBornProgram } from '@/features/programs/widgets/newborn/NewbornProgram';
import {
    ActiveConsultationDto,
    ArticleDto,
    MenopauseProgramType,
    NewbornProgram,
    NnSundvaegtProgram,
    ProductCardViewModel,
    ProgramFilterTagDefinition,
    SundvaegtProgram,
} from '@/features/programs/model/types';
import { SundVaegtProgram } from '@/features/programs/widgets/sundvaegt/SundvaegtProgram';
import { NnSundVaegtProgram } from '@/features/programs/widgets/sundvaegt-sammen/NnSundvaegtProgram';
import { MenopauseProgram } from '@/features/programs/widgets/menopause/MenopauseProgram';
import { useEffect } from 'react';
import {
    adolescentMinimumAgeAtom,
    adultMinimumAgeAtom,
    isAdolescentEnabledAtom,
} from '@/caregiverSpace/shared/model';

type CareProgram = 'MENOPAUSE' | 'NEWBORN' | 'SUNDVAEGT' | 'NNSUNDVAEGT';

interface AgeOptions {
    AdolescentMinimumAge?: number;
    AdultMinimumAge?: number;
}

export interface ProgramProps {
    type: CareProgram;
    productCardViewModels: ProductCardViewModel[];
    menopauseProgram?: MenopauseProgramType;
    newbornProgram?: NewbornProgram;
    sundvaegtProgram?: SundvaegtProgram;
    nnSundvaegtProgram?: NnSundvaegtProgram;
    upcomingConsultation?: ActiveConsultationDto;
    articles?: ArticleDto[];
    filterTags?: ProgramFilterTagDefinition[];
    isAdolescentEnabled?: boolean;
    ageOptions?: AgeOptions;
}

export function ProgramsApp(
    containerQuery: string,
    programProps: ProgramProps,
) {
    const elm = document.querySelector(containerQuery);
    const root = createRoot(elm);
    root.render(<App {...programProps} />);
}

function App(props: ProgramProps) {
    useEffect(() => {
        document.getElementById('program-spinner')?.remove();
    }, []);

    useHydrateAtoms([
        [adolescentMinimumAgeAtom, props.ageOptions?.AdolescentMinimumAge],
        [adultMinimumAgeAtom, props.ageOptions?.AdultMinimumAge],
        [isAdolescentEnabledAtom, props.isAdolescentEnabled],
    ]);

    return (
        <>
            {props.type === 'MENOPAUSE' && (
                <MenopauseProgram
                    productCardViewModels={props.productCardViewModels}
                    menopauseProgram={props.menopauseProgram}
                    upcomingConsultation={props.upcomingConsultation}
                    articles={props.articles}
                />
            )}
            {props.type === 'NEWBORN' && (
                <NewBornProgram
                    productCardViewModels={props.productCardViewModels}
                    newbornProgram={props.newbornProgram}
                    articles={props.articles}
                />
            )}
            {props.type === 'SUNDVAEGT' && (
                <SundVaegtProgram
                    productCardViewModels={props.productCardViewModels}
                    sundvaegtProgram={props.sundvaegtProgram}
                    articles={props.articles}
                    filterTags={props.filterTags}
                />
            )}
            {props.type === 'NNSUNDVAEGT' && (
                <NnSundVaegtProgram
                    productCardViewModels={props.productCardViewModels}
                    nnSundvaegtProgram={props.nnSundvaegtProgram}
                    upcomingConsultation={props.upcomingConsultation}
                    articles={props.articles}
                    filterTags={props.filterTags}
                />
            )}
        </>
    );
}
